/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create models using Sigma", description = "Connect Hightouch to your Sigma instance and build Hightouch models using existing workbooks.", lastUpdatedDate = new Date(1682035200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create models using Sigma",
        description: "Connect Hightouch to your Sigma instance and build Hightouch models using existing workbooks.",
        lastUpdatedDate: "Fri Apr 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Supported sources",
          "depth": 0,
          "id": "supported-sources"
        }, {
          "value": "Connect to Sigma",
          "depth": 0,
          "id": "connect-to-sigma"
        }, {
          "value": "Build models",
          "depth": 0,
          "id": "build-models"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Worksbooks aren't visible in Hightouch",
          "depth": 1,
          "id": "worksbooks-arent-visible-in-hightouch"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      em: "em",
      h2: "h2",
      ul: "ul",
      li: "li",
      strong: "strong",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["If you've already invested in modeling business data in ", _jsx(_components.a, {
          href: "https://www.sigmacomputing.com/",
          children: "Sigma"
        }), ", you may want to use your Sigma workbooks as the basis for ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "Hightouch models"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "When you import a model from Sigma, Hightouch converts your workbook element into a SQL query and runs it directly against your data warehouse. As you publish changes in Sigma, your Hightouch model updates to reflect the latest version of your Sigma workbook."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This extension uses Sigma as a ", _jsx(_components.em, {
            children: "modeling method"
          }), ", not a ", _jsx(_components.em, {
            children: "data source"
          }), ". In other words, Sigma provides the SQL query, not the query results. Before enabling this extension, you must first ", _jsx(_components.a, {
            href: "/getting-started/create-your-first-sync#connect-a-source",
            children: "create a source"
          }), " in Hightouch that connects to the same data warehouse used by Sigma."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-sources",
        children: "Supported sources"
      }), "\n", _jsx(_components.p, {
        children: "You can create models based on Sigma workbooks for the following sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/greenplum",
            children: "Greenplum Database"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sigma",
        children: "Connect to Sigma"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, go to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/sigma/configuration",
          children: _jsx(_components.strong, {
            children: "Sigma configuration page"
          })
        }), " in ", _jsx(_components.strong, {
          children: "Extensions"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sigma cloud"
          }), " - Follow ", _jsx(_components.a, {
            href: "https://help.sigmacomputing.com/hc/en-us/articles/360037430573-Technical-Specifications#h_01EJF3BT46HVZ8YMNFQ149SD0A",
            children: "these instructions"
          }), " to find your Sigma cloud."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client ID"
          }), " - Follow ", _jsx(_components.a, {
            href: "https://help.sigmacomputing.com/hc/en-us/articles/4408555307027-Get-an-API-Token-and-Client-ID",
            children: "these instructions"
          }), " to find your client ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client secret"
          }), " - Follow ", _jsx(_components.a, {
            href: "https://help.sigmacomputing.com/hc/en-us/articles/4408555307027-Get-an-API-Token-and-Client-ID",
            children: "these instructions"
          }), " to create a secret tied to your client ID. Make sure to create an ", _jsx(_components.strong, {
            children: "API token"
          }), " rather than ", _jsx(_components.strong, {
            children: "Embed secret"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "build-models",
        children: "Build models"
      }), "\n", _jsxs(_components.p, {
        children: ["After configuring your Sigma credentials, you can create a model using a ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "source that supports Sigma"
        }), ". Select your source and then choose ", _jsx(_components.strong, {
          children: "Import Model from Sigma"
        }), " as your modeling method."]
      }), "\n", _jsxs(_components.p, {
        children: ["On the next screen, you can select which Sigma ", _jsx(_components.strong, {
          children: "Workbook"
        }), ", ", _jsx(_components.strong, {
          children: "Page"
        }), ", and ", _jsx(_components.strong, {
          children: "Element"
        }), " to build your Hightouch model off of. Click ", _jsx(_components.strong, {
          children: "View SQL"
        }), " to see the query that Hightouch executes on your data warehouse. Make sure to click ", _jsx(_components.strong, {
          children: "Preview"
        }), " to preview your results before finishing your model setup."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/create-model-sigma.png",
          alt: "Creating a Sigma-based model in the Hightouch app"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "worksbooks-arent-visible-in-hightouch",
        children: "Worksbooks aren't visible in Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only displays ", _jsx(_components.a, {
          href: "https://help.sigmacomputing.com/hc/en-us/articles/1500010372322-Workbook-Lifecycle-Explore-Draft-and-Publish#h_01F6JP3G04NC4APKVE1DQ7ESH8",
          children: "published"
        }), " Sigma workbooks, to which the credentials ", _jsx(_components.a, {
          href: "#connect-to-sigma",
          children: "you entered into Hightouch"
        }), " have access."]
      }), "\n", _jsx(_components.p, {
        children: "If you don't see all the workbooks you expect in Hightouch, make sure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The workbook is published (not in draft mode)."
        }), "\n", _jsxs(_components.li, {
          children: ["The user whose credentials you used to ", _jsx(_components.a, {
            href: "#connect-to-sigma",
            children: "connect to Sigma"
          }), " has at least ", _jsx(_components.strong, {
            children: "view"
          }), " access to the workbook; if they don't, share it with them and give them at least view access."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/share-sigma-workbook.png",
          alt: "Sharing a workbook"
        })
      })]
    });
  }
}
export default MDXContent;
